import Vue from 'vue';
import Vuex, { StoreOptions } from 'vuex';
import { RootState, UpdateFormControlConfigPayload, UpdateFormControlPayload, SetCurrentStepPayload } from './types';

import { user } from './user';
import { startForm } from './start';
import { quoteForm } from './quote';
import { additionalForm } from './additional';
import { endorsementForm } from './endorsement';
import { resource } from './resource';
import { lock } from './lock';
import { account } from './account';
import { health } from './health';
import { submissionState } from './submission';
import { getNonNullAliasValue, getControlObj, setControlDataServiceValue, getControlObjFromV2DataPath, getControlObjByNameOrAlias } from '@/scripts/helper';
import { showControlHelper } from '@/scripts/show';
import { Toast } from '@/api/index.defs';
import ToastContent from '@/components/shared/ToastContent.vue';
import { FeatureFlagsService } from '@/api/FeatureFlagsService';
import Environment from '@/scripts/environment';

export function setControlOnFormData (formData: any, controlObject: any, value: any = null) {
    if (!formData) {
        return;
    }
    Vue.set(formData, controlObject.name, {
        "value": value ?? controlObject.value,
        "DataServiceValue": controlObject.DataServiceValue,
        "DataServiceType": controlObject.DataServiceType,
        "SourceValue": controlObject.SourceValue,
        "metadata": controlObject.metadata,
        "config": controlObject.config
    });
}
function buildDataObject (nameParts: any, payloadValue: any, newControlValue: any) {
    nameParts.slice(1).reduce((node: any, namePart: any, i: number) => {
        const nodeValue = (nameParts.length - 1) === ++i ? payloadValue : node[namePart] || {};
        node[namePart] = nodeValue;
        return nodeValue;
        }, newControlValue);
    return newControlValue;
}

function processV2ContractPath (state: any, payload: any, controlObj: any | null) {
    const nameParts = controlObj?.v2DataPath?.split('.') ?? payload.v2DataPath?.split('.') ?? payload.name.split('.') ?? [];
    if (nameParts.length >= 1) {
        state[payload.module].form.data = state[payload.module].form.data ?? {};
        if (!state[payload.module].form.data[nameParts[0]]) {
            Vue.set(state[payload.module].form.data, nameParts[0], {});
        }
        let newValue = state[payload.module].form.data[nameParts[0]]?.value ?? {};

        if (nameParts.length === 1) {
            newValue = payload.value;
        } else {
            newValue = buildDataObject(nameParts, payload.value, newValue);
        }

        setControlOnFormData(state[payload.module].form.data, { name: nameParts[0], value: newValue });
    }
}

Vue.use(Vuex);
const store: StoreOptions<RootState> = {
    state: {
        blockIncrementer: 0,
        statuses: {},
        currentStep: null,
        messageHubMessageIds: [],
        formStateEvents: {},
        featureFlags: {}
    },
    mutations: {
        blockStart (state) {
            state.blockIncrementer++;
        },
        blockComplete (state) {
            if (state.blockIncrementer > 0) {
                state.blockIncrementer--;
            } else {
                state.blockIncrementer = 0;
            }
        },
        updateFormFromContract (state: any, payload: any) {
            let controlObj = getControlObjFromV2DataPath(state[payload.module].form, payload.v2DataPath);
            processV2ContractPath(state, payload, controlObj);
            if (controlObj) {
                Vue.set(controlObj, 'value', payload.value);
                setControlOnFormData(state[payload.module].form.data, controlObj, payload.value);
                const submission = state["submissionState"].submission;
                if (submission?.Data && submission?.Data[payload.name]?.value !== payload.value) {
                    Vue.set(state["submissionState"].submission.Data, payload.name, { "value": payload.value });
                }
            }
        },
        updateFormControlConfig (state: any, payload: UpdateFormControlConfigPayload) {
            if (!payload.name) return;

            if (!state[payload.module]) {
                return;
            }

            if (!state[payload.module].form || !state[payload.module].form.data) {
                return;
            }

            const controlObj = getControlObj(state[payload.module].form, payload.name);
            if (controlObj) {
                Vue.set(controlObj, 'config', payload.config);
                setControlOnFormData(state[payload.module].form.data, controlObj);
            }
        },
        updateFormControl (state: any, payload: UpdateFormControlPayload) {
            if (!payload.name) {
                return;
            }
            const submission = state["submissionState"].submission;
            if (submission?.Data && submission?.Data[payload.name]?.value !== payload.value && !payload.name?.includes('.')) {
                if (payload.dataServiceValue) {
                    Vue.set(state["submissionState"].submission.Data, payload.name, { "value": payload.value, "DataServiceValue": payload.dataServiceValue });
                } else {
                    Vue.set(state["submissionState"].submission.Data, payload.name, { "value": payload.value });
                }
            }
            if (!state[payload.module]) {
                return;
            }

            if (state[payload.module].form) {
                if (payload.name?.includes('.')) {
                    const controlName = payload.name.split('.')[0];
                    const controlObj = getControlObj(state[payload.module].form, controlName);
                    processV2ContractPath(state, payload, null);
                    if (controlObj) {
                        setControlOnFormData(state[payload.module].form.data, controlObj);
                    }
                }
                const controlObj = getControlObj(state[payload.module].form, payload.name);
                if (controlObj) {
                    if (controlObj.alias) {
                        const aliasControlObject = getControlObj(state[payload.module].form, controlObj.alias);
                        if (submission?.Data && submission?.Data[aliasControlObject.name]?.value !== payload.value) {
                            Vue.set(state["submissionState"].submission.Data, aliasControlObject.name, { "value": payload.value });
                            setControlOnFormData(state[payload.module].form.data, aliasControlObject);
                        }
                        if (aliasControlObject.value !== payload.value) {
                            Vue.set(aliasControlObject, 'value', payload.value);
                            setControlOnFormData(state[payload.module].form.data, aliasControlObject);
                        }
                    }
                    const aliasValue = getNonNullAliasValue(state[payload.module].form, payload.name, state["submissionState"]?.submission?.Data);
                    if (!aliasValue && controlObj.value !== payload.value) {
                        Vue.set(controlObj, 'value', payload.value);
                    }
                    if (aliasValue && (controlObj.value == null || controlObj.value !== aliasValue)) {
                        Vue.set(controlObj, 'value', aliasValue);
                    }

                    processV2ContractPath(state, payload, controlObj);
                    setControlOnFormData(state[payload.module].form.data, controlObj);
                } else if (payload.alias !== null && payload.alias !== undefined) {
                    if (submission?.Data && submission?.Data[payload.alias]?.value !== payload.value) {
                        Vue.set(state["submissionState"].submission.Data, payload.alias, { "value": payload.value });
                    }
                }
            }
        },
        updateFormControlMetadata (state: any, payload: any) {
            const controlObj = getControlObj(state[payload.module].form, payload.name);
            if (controlObj) {
                Vue.set(controlObj, 'metadata', payload.metadata);
            }
            const submission = state["submissionState"].submission;
            if (submission?.Data && !submission?.Data[payload.name]) {
                Vue.set(state["submissionState"].submission.Data, payload.name, {});
            }
            Vue.set(state["submissionState"].submission.Data[payload.name], "metadata", payload.metadata);
        },
        clearFormControlMetadata (state: any, payload: any) {
            const controlObj = getControlObj(state[payload.module].form, payload.name);
            if (controlObj) {
                controlObj.metadata = null;
            }
        },
        updateFormControlDataServiceValue (state: any, payload: any) {
            if (payload.dataServiceValue == null) {
                return;
            }
            const submission = state["submissionState"].submission;
            if (submission?.Data) {
                if (!submission?.Data[payload.name]) {
                    Vue.set(state["submissionState"].submission.Data, payload.name, {});
                }
                const submissionValueExists = submission.Data[payload.name].value !== null && submission.Data[payload.name].value !== undefined;
                if (!submissionValueExists && !payload.controlObj?.config?.ignoreDataServiceValue) {
                    Vue.set(state["submissionState"].submission.Data[payload.name], "value", payload.dataServiceValue);
                }
                Vue.set(state["submissionState"].submission.Data[payload.name], "DataServiceValue", payload.dataServiceValue);
            }
            const controlObj = getControlObj(state[payload.module].form, payload.name);
            const controlObjValueExists = controlObj?.value !== null && controlObj?.value !== undefined;
            if (!controlObjValueExists && !payload.controlObj?.config?.ignoreDataServiceValue) {
                Vue.set(controlObj, 'value', payload.dataServiceValue);
            }
            Vue.set(controlObj, 'DataServiceValue', payload.dataServiceValue);
            setControlOnFormData(state[payload.module].form.data, controlObj);
        },
        updateFormControlDataServiceValues (state: any, payload: any) {
            if (!payload.Data ||
                !state['quoteForm'] || !state['quoteForm'].form ||
                payload.SubmissionId != state['quoteForm'].form.ResultId) {
                console.debug(`updateFormControlDataServiceValues: Ignored update, current submissionId:${payload.SubmissionId}`, payload);
                return;
            }

            const keys = Object.keys(payload.Data);
            keys.forEach((k: string) => {
                const o = payload.Data[k];
                if (o && o.DataServiceType) {
                    setControlDataServiceValue('quoteForm', k, o.DataServiceValue, o.DataServiceType);
                }
            });
        },
        updateSubmissionRisk (state: any, risk: any) {
          if (!risk) {
              console.debug('updateRiskKey: Ignored update, risk is null');
              return;
          }

          const controlObj = getControlObj(state['quoteForm'].form, 'Risk');
          Vue.set(controlObj, 'value', risk);
          Vue.set(state["submissionState"].submission.Data.Risk, "value", risk);
        },
        setCurrentStepNumber (state: any, payload: SetCurrentStepPayload) {
            state[payload.module].currentStepNumber = payload.stepNumber;
        },
        addMessageHubMessageId (state: any, id: any) {
            state.messageHubMessageIds.push(id);
        },
        updateFeatureFlag (state: any, payload: any) {
            Vue.set(state.featureFlags, payload.featureName, payload.isEnabled);
        },
        // keep track of the most recent form state events to ensure responding data service matches local state version
        registerFormStateEvent: (state: any, payload: any) => { // {contextId: string, eventType: string, eventId: string}
            if (!state.formStateEvents[payload.contextId]) state.formStateEvents[payload.contextId] = {};
            state.formStateEvents[payload.contextId][payload.eventType] = payload.eventId;
        }
    },
    actions: {
        toast ({ commit }, data: Toast) {
            Vue.$toast({ component: ToastContent, props: { Message: data.Message, Link: data.Link } });
        },
        toastError ({ commit }, data: Toast) {
            Vue.$toast.error(data.Message);
        },
        toastSuccess ({ commit }, data: Toast) {
            Vue.$toast.success(data.Message, { timeout: 5000 });
        },
        getFeatureFlags (context) {
            const featureFlags = Environment.FEATURE_FLAGS || [];
            featureFlags.forEach((featureName: string) => {
                FeatureFlagsService.isEnabled({ featureName })
                .then(isEnabled => {
                    context.commit('updateFeatureFlag', { featureName, isEnabled });
                })
                .catch((e) => {
                    console.log(e);
                });
            });

            // refetch after 30 minutes
            setTimeout(() => {
                context.dispatch('getFeatureFlags');
            }, 1800000);
        }
    },
    getters: {
        showUWMenu: (state: any) => {
            return true;
            // return this.$router.name != 'Dashboard';
        },
        showBlockUI: (state: any) => {
            return state.httpCount > 0;
        },
        getMessageHubMessageAlreadyReceived: (state: any) => (id: any) => {
            const result = state.messageHubMessageIds.find((o: string) => o == id);
            if (result) return true;
            return false;
        },
        // determine if the passed event matches the current local state version
        formStateEventIsActive: (state: any) => (contextId: string, eventType: string, eventId: string) => {
            if (!state.formStateEvents[contextId]) return false;
            if (!state.formStateEvents[contextId][eventType]) return false;
            if (state.formStateEvents[contextId][eventType] == eventId) return true;

            return false;
        },
        showStep: (state: any, getters: any, rootState: any, rootGetters: any) => (module: string, step: any) => {
            if (!rootState[module]) return false;
            if (!step) return false;
            if (!step['show']) return true;
            const show = showControlHelper(module, step['show']);
            return show;
        },
        showSection: (state: any, getters: any, rootState: any, rootGetters: any) => (module: string, section: any) => {
            if (!rootState[module]) return false;
            if (!section) return false;

            if (section['show']) {
                const show = showControlHelper(module, section['show']);
                if (show == false) return show;
            }

            // hide if no controls in section are visible
            let visibleControl = false;

            if (!section.controls) return true;

            section.controls.forEach((c: any) => {
                const controlObj = getControlObj(rootState[module].form, c.name);
                if (controlObj && (!controlObj.config || !controlObj.config['show'])) {
                    visibleControl = true;
                } else if (controlObj && controlObj.config['show']) {
                    const showC = showControlHelper(module, controlObj.config['show']);
                    if (showC) visibleControl = true;
                }
            });

            return visibleControl;
        },
        showControl: (state: any, getters: any, rootState: any, rootGetters: any) => (module: string, name: string, policy?: any | null) => {
            if (!rootState[module]) return false;
            const controlObj = getControlObj(rootState[module].form, name);
            if (!controlObj) return false;
            if (!controlObj.config || !controlObj.config['show']) return true;
            const show = showControlHelper(module, controlObj.config['show'], null, name, policy);
            return show;
        },
        getControlValue: (state: any, getters: any, rootState: any, rootGetters: any) => (module: string, name: string) => {
            if (!rootState[module]) return null;

            let value = null;
            const form = rootState[module].form;
            if (!form) {
                return null;
            }
            const controlObj = getControlObjByNameOrAlias(rootState[module].form, name);

            if (controlObj) {
                value = controlObj['value'];
            } else if (form && form.data && form.data[name]) {
                value = form.data[name].value;
            }

            return value;
        },
        getNonNullAliasObj: (rootState: any) => (module: string, aliasName: string) => {
            let returnControl = null as any;
            const form = rootState[module].form;
            if (!form) {
                return null;
            }
            form.steps?.forEach((step: any) => {
                step.sections?.forEach((section: any) => {
                    section.controls?.forEach((control: any) => {
                        if (control.alias == aliasName && control.value) {
                            returnControl = control;
                        }
                    });
                });
            });
            return returnControl;
        },
        getControl: (state: any, getters: any, rootState: any, rootGetters: any) => (module: string, name: string, property: string) => {
            if (!rootState[module]) return null;
            const controlObj = getControlObj(rootState[module].form, name);
            return controlObj;
        },
        getNonNullAliasValue: (state: any, getters: any, rootState: any, rootGetters: any) => (module: string, aliasName: string, includeSubmissionData: boolean = true) => {
            return getNonNullAliasValue(rootState[module].form, aliasName, includeSubmissionData ? rootState["submissionState"]?.submission?.Data : null);
        },
        getSubmissionDataValue: (state: any, getters: any, rootState: any, rootGetters: any) => (name: string): any => {
            const submission = rootGetters['submissionState/getSubmission'];
            if (submission && submission.Data && submission.Data[name]) {
                return submission.Data[name]?.value;
            }
        },
        getSubmissionValue: (state: any, getters: any, rootState: any, rootGetters: any) => (name: string): any => {
            const submission = rootGetters['submissionState/getSubmission'];
            if (submission && submission[name]) {
                return submission[name];
            }
        },
        getFormDataValue: (state: any, getters: any, rootState: any, rootGetters: any) => (module: string, name: string): any => {
            if (!rootState[module] || !rootState[module].form) return null;
            if (!rootState[module].form.data) return null;
            return rootState[module].form.data[name];
        },
        getCurrentStepNumber: (state: any, getters: any, rootState: any, rootGetters: any) => (module: string) => {
            if (!rootState[module]) return false;
            if (!rootState[module].currentStepNumber) { return 0; }
            return rootState[module].currentStepNumber;
        },
        getCurrentStep: (state: any, getters: any, rootState: any, rootGetters: any) => (module: string): any => {
            if (!rootState[module] || !rootState[module].form) return null;
            return rootState[module].form.steps[rootState[module].currentStepNumber];
        },
        getForm: (state: any, getters: any, rootState: any, rootGetters: any) => (module: string) => {
            if (!rootState[module]) return null;
            return rootState[module].form;
        },
        isFeatureFlagEnabled: (state: any) => (featureName: string) => {
            return state.featureFlags[featureName] || false;
        }
    },
    modules: {
        user,
        startForm,
        quoteForm,
        additionalForm,
        endorsementForm,
        resource,
        lock,
        account,
        health,
        submissionState
    }
};

export default new Vuex.Store<RootState>(store);
